import { FeatureToggleName, IFeatureToggle } from './featureToggles.types';
import { isConfigToggle } from './featureToggles.utils';

/**
 * Where the runtime toggle file is served from. `static/feature-toggles.json`
 * is copied to the site root by the build, and the content server picks up an
 * edit to `public/feature-toggles.json` in the S3 bucket on its next sync
 * (about once a minute), so values can change without a rebuild.
 */
export const RUNTIME_TOGGLES_URL = `/feature-toggles.json`;

/** Give up on the runtime file after this long and keep the env config. */
export const RUNTIME_TOGGLES_TIMEOUT_MS = 3000;

const knownNames = new Set<string>(Object.values(FeatureToggleName));

/**
 * Validate the runtime file's body: `{ "toggles": [{ "name", "isEnabled" }] }`.
 * Entries with an unknown name, a `Config_` name or a non-boolean value are
 * dropped with a warning, so one bad entry cannot break the rest.
 */
export const parseRuntimeToggles = (body: unknown): IFeatureToggle[] => {
    const toggles = (body as { toggles?: unknown } | null)?.toggles;
    if (!Array.isArray(toggles)) {
        console.warn(`Feature toggles: runtime file has no "toggles" list`);
        return [];
    }

    return toggles.filter((entry): entry is IFeatureToggle => {
        const { name, isEnabled } = (entry ?? {}) as Record<string, unknown>;
        const isValid =
            typeof name === `string` &&
            knownNames.has(name) &&
            !isConfigToggle(name as FeatureToggleName) &&
            typeof isEnabled === `boolean`;
        if (!isValid) {
            console.warn(`Feature toggles: ignoring runtime entry`, entry);
        }
        return isValid;
    });
};

/**
 * Fetch the runtime toggle list. Never throws: a missing file, a network
 * error, a timeout or an invalid body all resolve to `[]`, which leaves the
 * env config in charge. Browser only; the caller runs it after mount.
 */
export const fetchRuntimeToggles = async (
    url: string = RUNTIME_TOGGLES_URL,
    timeoutMs: number = RUNTIME_TOGGLES_TIMEOUT_MS,
): Promise<IFeatureToggle[]> => {
    if (typeof fetch !== `function`) return [];

    const controller =
        typeof AbortController === `function` ? new AbortController() : null;
    const timer = setTimeout(() => controller?.abort(), timeoutMs);

    try {
        const response = await fetch(url, {
            cache: `no-store`,
            signal: controller?.signal,
        });
        // A missing file is the normal "no runtime values" case, not an error.
        if (!response.ok) return [];
        return parseRuntimeToggles(await response.json());
    } catch (error) {
        console.warn(`Feature toggles: runtime file unavailable`, error);
        return [];
    } finally {
        clearTimeout(timer);
    }
};
